import React, { useState } from 'react';
import { Lead, User } from '@/lib/api';
import { cn } from '@/lib/utils';
import {
  UserPlus,
  ArrowRight,
  Flag,
  Trash2,
  Download,
  X,
  Loader2,
  AlertTriangle
} from 'lucide-react';
import { exportSelectedLeads } from '@/utils/csvExport';

interface BulkActionToolbarProps {
  selectedLeads: Lead[];
  allUsers: User[];
  onClearSelection: () => void;
  onBulkAssign: (leadIds: string[], userId: string) => Promise<void>;
  onBulkChangeStage: (leadIds: string[], stage: Lead['stage']) => Promise<void>;
  onBulkChangePriority: (leadIds: string[], priority: Lead['priority']) => Promise<void>;
  onBulkDelete: (leadIds: string[]) => Promise<void>;
}

type ActionType = 'assign' | 'stage' | 'priority' | 'delete' | null;

export const BulkActionToolbar: React.FC<BulkActionToolbarProps> = ({
  selectedLeads,
  allUsers,
  onClearSelection,
  onBulkAssign,
  onBulkChangeStage,
  onBulkChangePriority,
  onBulkDelete,
}) => {
  const [showDialog, setShowDialog] = useState<ActionType>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string>('');

  const leadIds = selectedLeads.map(lead => lead.id);

  const handleAction = async () => {
    if (!selectedValue && showDialog !== 'delete') return;

    setIsLoading(true);
    try {
      switch (showDialog) {
        case 'assign':
          await onBulkAssign(leadIds, selectedValue);
          break;
        case 'stage':
          await onBulkChangeStage(leadIds, selectedValue as Lead['stage']);
          break;
        case 'priority':
          await onBulkChangePriority(leadIds, selectedValue as Lead['priority']);
          break;
        case 'delete':
          await onBulkDelete(leadIds);
          break;
      }
      setShowDialog(null);
      setSelectedValue('');
      onClearSelection();
    } catch (error) {
      console.error('Bulk action failed:', error);
      alert('Failed to perform bulk action. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = () => {
    exportSelectedLeads(selectedLeads);
  };

  const openDialog = (action: ActionType) => {
    setShowDialog(action);
    setSelectedValue('');
  };

  const closeDialog = () => {
    setShowDialog(null);
    setSelectedValue('');
  };

  if (selectedLeads.length === 0) return null;

  return (
    <>
      {/* Toolbar */}
      <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-40 animate-in slide-in-from-bottom-5">
        <div className="bg-white rounded-lg shadow-2xl border border-gray-200 p-4">
          <div className="flex items-center gap-4">
            {/* Selection count */}
            <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg font-medium">
              <span className="text-sm">
                {selectedLeads.length} lead{selectedLeads.length > 1 ? 's' : ''} selected
              </span>
              <button
                onClick={onClearSelection}
                className="p-1 hover:bg-blue-100 rounded transition-colors"
                title="Clear selection"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Divider */}
            <div className="h-8 w-px bg-gray-300" />

            {/* Action buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => openDialog('assign')}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Assign to user"
              >
                <UserPlus className="w-4 h-4" />
                Assign
              </button>

              <button
                onClick={() => openDialog('stage')}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Change stage"
              >
                <ArrowRight className="w-4 h-4" />
                Stage
              </button>

              <button
                onClick={() => openDialog('priority')}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Change priority"
              >
                <Flag className="w-4 h-4" />
                Priority
              </button>

              <button
                onClick={handleExport}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Export to CSV"
              >
                <Download className="w-4 h-4" />
                Export
              </button>

              <button
                onClick={() => openDialog('delete')}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Delete selected"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Dialogs */}
      {showDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {showDialog === 'assign' && 'Assign Leads'}
                {showDialog === 'stage' && 'Change Stage'}
                {showDialog === 'priority' && 'Change Priority'}
                {showDialog === 'delete' && 'Delete Leads'}
              </h3>

              {showDialog === 'delete' ? (
                <div className="mb-6">
                  <div className="flex items-start gap-3 p-4 bg-red-50 rounded-lg border border-red-200">
                    <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-red-800 font-medium mb-1">
                        This action cannot be undone
                      </p>
                      <p className="text-sm text-red-700">
                        Are you sure you want to delete {selectedLeads.length} lead
                        {selectedLeads.length > 1 ? 's' : ''}?
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {showDialog === 'assign' && 'Select User'}
                    {showDialog === 'stage' && 'Select Stage'}
                    {showDialog === 'priority' && 'Select Priority'}
                  </label>
                  <select
                    value={selectedValue}
                    onChange={(e) => setSelectedValue(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isLoading}
                  >
                    <option value="">-- Select --</option>
                    {showDialog === 'assign' &&
                      allUsers.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name} ({user.role})
                        </option>
                      ))}
                    {showDialog === 'stage' &&
                      ['NEW', 'CONTACTED', 'DISCUSSION', 'PROPOSAL', 'NEGOTIATION', 'WON', 'LOST'].map((stage) => (
                        <option key={stage} value={stage}>
                          {stage}
                        </option>
                      ))}
                    {showDialog === 'priority' &&
                      ['HOT', 'WARM', 'COLD'].map((priority) => (
                        <option key={priority} value={priority}>
                          {priority}
                        </option>
                      ))}
                  </select>
                </div>
              )}

              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={closeDialog}
                  disabled={isLoading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAction}
                  disabled={isLoading || (!selectedValue && showDialog !== 'delete')}
                  className={cn(
                    'px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2',
                    showDialog === 'delete'
                      ? 'bg-red-600 hover:bg-red-700'
                      : 'bg-blue-600 hover:bg-blue-700'
                  )}
                >
                  {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {showDialog === 'delete' ? 'Delete' : 'Apply'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
